<script lang="ts">
  import { onMount } from 'svelte';
  import Button from '../common/Button.svelte';
  import EmptyState from '../common/EmptyState.svelte';
  import Badge from '../common/Badge.svelte';
  import BudgetCard from './BudgetCard.svelte';
  import BudgetSetupModal from './BudgetSetupModal.svelte';
  import FormSelect from '../common/FormSelect.svelte';
  import Filter from '../icons/Filter.svelte';
  import MonthYearSelector from './MonthYearSelector.svelte';
  import RadioButtonGroup from '../common/RadioButtonGroup.svelte';
  import type { Transaction } from '../../lib/types.js';
  import { calculateAverageMonthlyIncome, formatCurrency } from '../../lib/analytics.js';
  import { getUserPreferences, type InfoDensity } from '../../lib/userPreferences.js';
  import { markChecklistItem } from '../../lib/onboardingState';
  import IncomeBudgetCard from './IncomeBudgetCard.svelte';
  import BudgetSuggestions from './BudgetSuggestions.svelte';
  import type { BudgetSuggestion } from '../../lib/budget/suggestions';

  type Budget = {
    name: string;
    categories: string[];
    limit: number;
    type: 'monthly' | 'quarterly' | 'yearly';
    allowRollover: boolean;
    resetRolloverYearly?: boolean;
    isFixedExpense: boolean;
    rolloverAmount: number;
  };

  type BudgetData = {
    version: number;
    budgets: Budget[];
    settings: {
      autoResetRolloverAtYearEnd: boolean;
      monthlyIncomeTarget?: number;
    };
  };

  type BudgetStatus = {
    name: string;
    categories: string[];
    type: 'monthly' | 'quarterly' | 'yearly' | 'unbudgeted';
    limit: number;
    spent: number;
    remaining: number;
    percentage: number;
    allowRollover?: boolean;
    resetRolloverYearly?: boolean;
    isFixedExpense?: boolean;
    rolloverAmount?: number;
    totalLimit?: number;
    isUnbudgeted?: boolean;
    // Legacy support for unbudgeted items
    category?: string;
  };

  let budgets = $state<BudgetData>({ version: 3, budgets: [], settings: { autoResetRolloverAtYearEnd: false } });
  let allBudgetStatus = $state<BudgetStatus[]>([]);
  let categories = $state<string[]>([]);
  let transactions = $state<Transaction[]>([]);
  let loading = $state(false);
  let error = $state<string | null>(null);
  let isModalOpen = $state(false);
  let selectedPeriod = $state('');

  // Filter/sort state
  let filterType = $state<'all' | 'monthly' | 'quarterly' | 'yearly'>('all');
  let sortBy = $state<'name' | 'amount' | 'percentage'>('name');
  let sortOrder = $state<'asc' | 'desc'>('asc');
  let showFilters = $state(false);

  // User preferences
  let preferences = $state(getUserPreferences());
  let infoDensity = $derived(preferences.infoDensity);

  // Calculate detected average monthly income
  const detectedMonthlyIncome = $derived(calculateAverageMonthlyIncome(transactions));

  // Get the monthly income target (user-set or detected)
  const monthlyIncomeTarget = $derived(
    budgets.settings.monthlyIncomeTarget || detectedMonthlyIncome
  );

  // Calculate total monthly budget allocation
  const totalMonthlyBudgets = $derived(() => {
    return budgets.budgets
      .filter(b => b.type === 'monthly')
      .reduce((sum, b) => sum + b.limit, 0);
  });

  // Calculate remaining income after budgets
  const remainingIncome = $derived(monthlyIncomeTarget - totalMonthlyBudgets());
  const remainingPercentage = $derived(
    monthlyIncomeTarget > 0 ? (remainingIncome / monthlyIncomeTarget) * 100 : 0
  );

  // Calculate actual income for selected period
  const actualIncomeForPeriod = $derived(() => {
    if (!selectedPeriod || transactions.length === 0) return 0;

    const isYearlyView = selectedPeriod.length === 4;

    const incomeTransactions = transactions.filter(t => {
      if (t.excluded) return false;
      if (t.amount <= 0) return false;
      const category = (t.category || '').toLowerCase();

      // Only include recurring income categories
      const recurringIncomeCategories = [
        'salary & wages',
        'interest & dividends'
      ];

      return recurringIncomeCategories.includes(category);
    });

    if (isYearlyView) {
      // Sum all income for the year
      const year = selectedPeriod;
      return incomeTransactions
        .filter(t => t.date.startsWith(year))
        .reduce((sum, t) => sum + t.amount, 0);
    } else {
      // Sum income for the specific month
      return incomeTransactions
        .filter(t => t.date.startsWith(selectedPeriod))
        .reduce((sum, t) => sum + t.amount, 0);
    }
  });

  // Initialize selected period to current month
  $effect(() => {
    if (!selectedPeriod) {
      const now = new Date();
      selectedPeriod = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
    }
  });

  onMount(async () => {
    await loadData();

    // Listen for preference changes
    const handlePreferencesUpdate = () => {
      preferences = getUserPreferences();
    };
    window.addEventListener('preferences-updated', handlePreferencesUpdate);

    return () => {
      window.removeEventListener('preferences-updated', handlePreferencesUpdate);
    };
  });

  async function loadData() {
    loading = true;
    error = null;

    try {
      await Promise.all([
        loadBudgets(),
        loadCategories(),
        loadBudgetStatus(),
        loadTransactions()
      ]);
    } catch (err: any) {
      error = err.message;
      console.error('Error loading budget data:', err);
    } finally {
      loading = false;
    }
  }

  async function loadBudgets() {
    const { getBudgets } = await import('../../lib/storage.js');
    budgets = await getBudgets();
  }

  async function loadCategories() {
    const { getCategories } = await import('../../lib/storage.js');
    categories = await getCategories();
   
  }

  async function loadTransactions() {
    const { getTransactions } = await import('../../lib/storage.js');
    transactions = await getTransactions();
   
  }

  async function loadBudgetStatus() {
    if (!selectedPeriod) return;

    const { getBudgetStatus } = await import('../../lib/storage.js');
    const data = await getBudgetStatus(selectedPeriod);

    // Check the view type
    const isYearlyView = selectedPeriod.length === 4;

    if (isYearlyView) {
      // For yearly view: show yearly budgets as-is, convert monthly to yearly (×12), convert quarterly to yearly (×4)
      const monthlyAsYearly = (data.monthly || []).map((budget: BudgetStatus) => ({
        ...budget,
        limit: budget.limit * 12,
        totalLimit: budget.totalLimit ? budget.totalLimit * 12 : budget.limit * 12,
        remaining: (budget.totalLimit ? budget.totalLimit * 12 : budget.limit * 12) - budget.spent,
        percentage: budget.spent / (budget.totalLimit ? budget.totalLimit * 12 : budget.limit * 12) * 100
      }));

      const quarterlyAsYearly = (data.quarterly || []).map((budget: BudgetStatus) => ({
        ...budget,
        limit: budget.limit * 4,
        totalLimit: budget.totalLimit ? budget.totalLimit * 4 : budget.limit * 4,
        remaining: (budget.totalLimit ? budget.totalLimit * 4 : budget.limit * 4) - budget.spent,
        percentage: budget.spent / (budget.totalLimit ? budget.totalLimit * 4 : budget.limit * 4) * 100
      }));

      allBudgetStatus = [
        ...monthlyAsYearly,
        ...quarterlyAsYearly,
        ...(data.yearly || []),
        ...(data.unbudgeted || [])
      ];
    } else {
      // Monthly view: show all budget types (quarterly budgets show their limit for the whole quarter)
      allBudgetStatus = [
        ...(data.monthly || []),
        ...(data.quarterly || []),
        ...(data.yearly || []),
        ...(data.unbudgeted || [])
      ];
    }
  }

  async function handleSaveBudget(name: string, categories: string[], type: 'monthly' | 'quarterly' | 'yearly', limit: number, allowRollover: boolean, isFixedExpense: boolean = false, resetRolloverYearly: boolean = false) {
    const { createBudget, updateBudget } = await import('../../lib/storage.js');

    // Check if budget exists (try to update first)
    const existing = budgets.budgets.find(b => b.name === name && b.type === type);

    if (existing) {
      // Update existing budget
      const result = await updateBudget(name, type, { categories, limit, allowRollover, resetRolloverYearly, isFixedExpense });
      if (!result.success) {
        throw new Error(result.error || 'Failed to update budget');
      }
    } else {
      // Create new budget
      const result = await createBudget({ name, categories, type, limit, allowRollover, resetRolloverYearly, isFixedExpense });
      if (!result.success) {
        throw new Error(result.error || 'Failed to create budget');
      }
    }

    await loadData();

    // Mark onboarding checklist item as complete
    markChecklistItem('budget');
  }

  async function handleDeleteBudget(name: string, type: 'monthly' | 'quarterly' | 'yearly') {
    const { deleteBudget } = await import('../../lib/storage.js');
    const result = await deleteBudget(name, type);

    if (!result.success) {
      throw new Error(result.error || 'Failed to delete budget');
    }

    await loadData();

    // Mark deleted budget as dismissed for 90 days to prevent re-suggesting
    if (budgetSuggestionsRef) {
      budgetSuggestionsRef.dismissCategory(name, 90);
    }
  }

  async function handleConvertType(name: string, categories: string[], fromType: 'monthly' | 'quarterly' | 'yearly', toType: 'monthly' | 'quarterly' | 'yearly', limit: number, allowRollover: boolean, isFixedExpense: boolean = false, resetRolloverYearly: boolean = false) {
    const { deleteBudget, createBudget } = await import('../../lib/storage.js');

    // Delete old budget
    const deleteResult = await deleteBudget(name, fromType);
    if (!deleteResult.success) {
      throw new Error(deleteResult.error || 'Failed to delete old budget');
    }

    // Create new budget with new type
    const createResult = await createBudget({ name, categories, type: toType, limit, allowRollover, resetRolloverYearly, isFixedExpense });
    if (!createResult.success) {
      throw new Error(createResult.error || 'Failed to create new budget');
    }

    await loadData();
  }

  async function handleResetRollover(name: string, type: 'monthly' | 'quarterly' | 'yearly') {
    const { resetBudgetRollover } = await import('../../lib/storage.js');
    const result = await resetBudgetRollover(name, type);

    if (!result.success) {
      throw new Error(result.error || 'Failed to reset rollover');
    }

    await loadData();
  }

  async function saveIncomeTarget(newTarget: number): Promise<void> {
    const { updateBudgetSettings } = await import('../../lib/storage.js');
    const result = await updateBudgetSettings({ monthlyIncomeTarget: newTarget });

    if (!result.success) {
      throw new Error(result.error || 'Failed to update income target');
    }

    await loadBudgets(); // Reload to get updated settings
  }

  async function clearIncomeTarget(): Promise<void> {
    const { updateBudgetSettings } = await import('../../lib/storage.js');
    const result = await updateBudgetSettings({ monthlyIncomeTarget: 0 });

    if (!result.success) {
      throw new Error(result.error || 'Failed to clear income target');
    }

    await loadBudgets();
  }

  // Budget suggestions handlers
  let editingSuggestion = $state<BudgetSuggestion | null>(null);
  let budgetSuggestionsRef: { refresh: () => void; dismissCategory: (name: string, days?: number) => void } | null = null;

  async function handleAcceptSuggestion(suggestion: BudgetSuggestion, skipDataReload = false): Promise<void> {
    const { createBudget } = await import('../../lib/storage.js');

    try {
      const result = await createBudget({
        name: suggestion.name,
        categories: suggestion.categories,
        type: suggestion.type,
        limit: suggestion.suggestedAmount,
        allowRollover: false,
        resetRolloverYearly: false,
        isFixedExpense: false
      });

      if (!result.success) {
        throw new Error(result.error || 'Failed to create budget');
      }

      // Only reload data if not skipping (used during bulk operations)
      if (!skipDataReload) {
        await loadData();
      }

      // Show success notification (could add toast here)
      console.log(`Budget created for ${suggestion.name}: ${formatCurrency(suggestion.suggestedAmount)}`);
    } catch (err) {
      console.error('Error accepting suggestion:', err);
      error = err instanceof Error ? err.message : 'Failed to accept suggestion';
    }
  }

  function handleCustomizeSuggestion(suggestion: BudgetSuggestion): void {
    editingSuggestion = suggestion;
    isModalOpen = true;
  }

  // Handle modal close - reset editing suggestion
  function handleModalClose() {
    isModalOpen = false;
    editingSuggestion = null;
  }

  // Build list of used categories to exclude from dropdown
  const existingBudgets = $derived(
    budgets.budgets.map(b => ({ name: b.name, categories: b.categories, type: b.type }))
  );

  const hasBudgets = $derived(
    budgets.budgets.length > 0
  );

  // Filter and sort budgets
  const filteredAndSortedBudgets = $derived(() => {
    let filtered = allBudgetStatus;

    // Apply type filter
    if (filterType !== 'all') {
      filtered = filtered.filter(b => b.type === filterType);
    }

    // Separate unbudgeted from regular budgets
    const unbudgetedItems = filtered.filter(b => b.isUnbudgeted || b.type === 'unbudgeted');
    const regularBudgets = filtered.filter(b => !b.isUnbudgeted && b.type !== 'unbudgeted');

    // Sort regular budgets
    const sorted = [...regularBudgets].sort((a, b) => {
      let comparison = 0;

      if (sortBy === 'name') {
        // Sort by name, fallback to category for unbudgeted items
        const aName = a.name || a.category || '';
        const bName = b.name || b.category || '';
        comparison = aName.localeCompare(bName);
      } else if (sortBy === 'amount') {
        comparison = a.spent - b.spent;
      } else if (sortBy === 'percentage') {
        comparison = a.percentage - b.percentage;
      }

      return sortOrder === 'asc' ? comparison : -comparison;
    });

    // Always put unbudgeted items at the end
    return [...sorted, ...unbudgetedItems];
  });

  // Watch for period changes
  $effect(() => {
    if (selectedPeriod) {
      loadBudgetStatus();
    }
  });
</script>

<div class="budget-dashboard">
  <div class="dashboard-header">
    <div>
      <h1>Budgets</h1>
      <p class="subtitle">Track your spending against monthly, quarterly, and yearly budgets</p>
    </div>
    <Button variant="primary" onclick={() => isModalOpen = true}>
      Add Budget
    </Button>
  </div>

  {#if loading}
    <div class="loading">Loading budgets...</div>
  {:else if error}
    <div class="error-message">{error}</div>
  {:else}
    <!-- Month/Year Selector -->
    <MonthYearSelector
      {selectedPeriod}
      onPeriodChange={(period) => {
        selectedPeriod = period;
        loadBudgetStatus();
      }}
    />

    <!-- Budget Suggestions -->
    <BudgetSuggestions
      bind:this={budgetSuggestionsRef}
      onAccept={handleAcceptSuggestion}
      onCustomize={handleCustomizeSuggestion}
      onBulkComplete={loadData}
    />

    <!-- Filters -->
    <div class="controls">
      <button class="filter-toggle" onclick={() => showFilters = !showFilters}>
        <Filter size={16} />
        Filters {showFilters ? '▲' : '▼'}
      </button>
    </div>

    {#if showFilters}
      <div class="filters">
        <div class="filter-group">
          <label>Type:</label>
          <RadioButtonGroup
            bind:value={filterType}
            options={[
              { value: 'all', label: 'All' },
              { value: 'monthly', label: 'Monthly' },
              { value: 'quarterly', label: 'Quarterly' },
              { value: 'yearly', label: 'Yearly' }
            ]}
            name="filter-type"
            layout="horizontal"
            size="sm"
          />
        </div>

        <div class="filter-group">
          <label>Sort by:</label>
          <RadioButtonGroup
            bind:value={sortBy}
            options={[
              { value: 'name', label: 'Name' },
              { value: 'amount', label: 'Amount' },
              { value: 'percentage', label: '% Used' }
            ]}
            name="sort-by"
            layout="horizontal"
            size="sm"
          />
        </div>

        <div class="filter-group">
          <label>Order:</label>
          <RadioButtonGroup
            bind:value={sortOrder}
            options={[
              { value: 'asc', label: 'Ascending' },
              { value: 'desc', label: 'Descending' }
            ]}
            name="sort-order"
            layout="horizontal"
            size="sm"
          />
        </div>
      </div>
    {/if}

    <!-- Budget List -->
    {#if !hasBudgets}
      <EmptyState
        title="No Budgets Yet"
        description="Create your first budget to start tracking your spending. You can add monthly budgets for recurring expenses, quarterly budgets for seasonal costs, or yearly budgets for annual expenses."
      />
    {:else if filteredAndSortedBudgets().length === 0}
      <EmptyState
        title="No Budgets Match Filters"
        description="Try adjusting your filters to see more budgets."
      />
    {:else}
      <div class="budget-count">
        Showing {filteredAndSortedBudgets().length} of {allBudgetStatus.length} budgets
      </div>

      <div class="budget-container">
        <!-- Income Budget Card -->
        <IncomeBudgetCard
          actualIncome={actualIncomeForPeriod()}
          incomeTarget={monthlyIncomeTarget}
          detectedIncome={detectedMonthlyIncome}
          isAutoDetected={!budgets.settings.monthlyIncomeTarget}
          density={infoDensity}
          onSaveTarget={saveIncomeTarget}
          onClearTarget={clearIncomeTarget}
        />

        {#each filteredAndSortedBudgets() as status (status.isUnbudgeted ? `${status.category}_${status.type}` : `${status.name}_${status.type}`)}
          <BudgetCard
            {status}
            period={selectedPeriod}
            density={infoDensity}
            availableCategories={categories}
            onEdit={handleSaveBudget}
            onDelete={handleDeleteBudget}
            onConvertType={handleConvertType}
            onResetRollover={handleResetRollover}
          />
        {/each}
      </div>
    {/if}
  {/if}
</div>

<BudgetSetupModal
  isOpen={isModalOpen}
  {categories}
  {existingBudgets}
  {transactions}
  {monthlyIncomeTarget}
  totalMonthlyBudgets={totalMonthlyBudgets()}
  initialData={editingSuggestion ? {
    name: editingSuggestion.name,
    categories: editingSuggestion.categories,
    type: editingSuggestion.type,
    limit: editingSuggestion.suggestedAmount,
    allowRollover: false,
    resetRolloverYearly: false,
    isFixedExpense: false
  } : undefined}
  onClose={handleModalClose}
  onSave={handleSaveBudget}
/>

<style>
  .budget-dashboard {
    max-width: 1400px;
    margin: 0 auto;
  }

  .dashboard-header {
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
    margin-bottom: var(--spacing-2xl);
  }

  .dashboard-header h1 {
    margin: 0 0 var(--spacing-xs) 0;
    font-size: var(--font-size-4xl);
    font-weight: var(--font-weight-bold);
    color: var(--color-text-primary);
  }

  .subtitle {
    margin: 0;
    color: var(--color-text-tertiary);
    font-size: var(--font-size-base);
  }

  .loading,
  .error-message {
    text-align: center;
    padding: var(--spacing-4xl);
    color: var(--color-text-tertiary);
  }

  .error-message {
    color: var(--color-danger);
    background: var(--color-danger-light);
    border-radius: var(--radius-lg);
  }

  .controls {
    display: flex;
    justify-content: flex-end;
    align-items: center;
    margin-bottom: var(--spacing-lg);
    gap: var(--spacing-lg);
  }

  .filter-toggle {
    display: flex;
    align-items: center;
    gap: var(--spacing-sm);
    padding: var(--spacing-sm) var(--spacing-md);
    background: var(--color-background);
    border: 1px solid var(--color-border);
    border-radius: var(--radius-md);
    font-size: var(--font-size-sm);
    font-weight: var(--font-weight-medium);
    color: var(--color-text-secondary);
    cursor: pointer;
    transition: all var(--transition-fast);
  }

  .filter-toggle:hover {
    border-color: var(--color-primary);
    color: var(--color-primary);
  }

  .filters {
    background: var(--color-background-secondary);
    border: 1px solid var(--color-border);
    border-radius: var(--radius-lg);
    padding: var(--spacing-lg);
    margin-bottom: var(--spacing-lg);
    display: flex;
    gap: var(--spacing-2xl);
    flex-wrap: wrap;
  }

  .filter-group {
    display: flex;
    flex-direction: column;
    gap: var(--spacing-sm);
  }

  .filter-group label {
    font-size: var(--font-size-xs);
    font-weight: var(--font-weight-semibold);
    color: var(--color-text-tertiary);
    text-transform: uppercase;
    letter-spacing: 0.5px;
  }

  .budget-count {
    font-size: var(--font-size-sm);
    color: var(--color-text-tertiary);
    margin-bottom: var(--spacing-md);
  }

  .budget-container {
    display: grid;
    grid-template-columns: 1fr;
    gap: var(--spacing-sm);
  }

  @media (max-width: 768px) {
    .dashboard-header {
      flex-direction: column;
      gap: var(--spacing-lg);
    }

    .controls {
      flex-direction: column;
      align-items: stretch;
    }

    .filters {
      flex-direction: column;
      gap: var(--spacing-lg);
    }

    .budget-container {
      grid-template-columns: 1fr;
    }
  }
</style>
